import { useListOffers } from '@/features/offers/api/list-offers';
import { useEffect, useMemo, useRef } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import { nextPromotionForCreative } from '../-components/ad-promotion/ad-promotion-options';
import { useAdWizard } from '../-context';
import type { AdWizardFormData } from '../-schema';

/** The page the promotion picker lists (shared query with the picker). */
export const AD_PROMOTION_LIST_PARAMS = { limit: 100 } as const;

/**
 * Keep the ad's promotion in step with its creative until the owner picks one.
 *
 * A generated graphic or video carries the promotion it was made for. When it
 * becomes the ad's creative, preselect that promotion in the picker so the
 * owner sees (and can change) what the ad will be linked to. Media is chosen
 * after the details step, so this runs at the wizard level, not in the field.
 * See {@link nextPromotionForCreative} for when it leaves the value alone.
 */
export function useCreativePromotionPreselect(
  form: UseFormReturn<AdWizardFormData>
) {
  const { selectedVideo } = useAdWizard();
  const { offers } = useListOffers(AD_PROMOTION_LIST_PARAMS);
  const preselectedRef = useRef<string | null>(null);

  const knownOfferIds = useMemo(
    () => new Set(offers.map((o) => o.id)),
    [offers]
  );
  const creativeOfferId = selectedVideo?.offerId ?? null;

  useEffect(() => {
    const next = nextPromotionForCreative({
      current: form.getValues('offerId'),
      ownerSelected: form.getFieldState('offerId').isDirty,
      preselected: preselectedRef.current,
      creativeOfferId,
      knownOfferIds,
    });
    if (!next) return;
    preselectedRef.current = next.preselected;
    form.setValue('offerId', next.value);
  }, [creativeOfferId, knownOfferIds, form]);
}
